import { selectAllWithdrawMethods, useWithdrawalMethods } from '@/feature/withdraw/hooks/use-withdrawal-methods';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { LightSdd, useGetAuthUserTrustlyInfo } from '@/hooks/use-user-trustly-information';

import {
    WithdrawVerificationOutcome,
    resolveWithdrawVerificationOutcome,
} from '../utils/resolve-withdraw-verification-outcome';

export const useNonWithdrawableVerificationCheck = () => {
    const { data: trustlyInfo, isLoading: isTrustlyInfoLoading } = useGetAuthUserTrustlyInfo();
    const globalSettingsFlags = useJurisdictionStore(state => state.jurisdictionSettings?.globalSettings?.featureFlags);
    const isNonWithdrawableCheckEnabled = globalSettingsFlags?.enable_non_withdrawable_check?.enabled ?? false;
    const isTrustlyVerificationEnabled = globalSettingsFlags?.trustly_withdrawal_verification_enabled?.enabled ?? true;
    const isIdpvVerificationEnabled = globalSettingsFlags?.idpv_withdrawal_verification_enabled?.enabled ?? false;

    // Only consulted to soften BLOCKED, which is reachable only when the Trustly path
    // flag is off (a likely outage): users with a method on file fall back to the
    // closed-loop flow instead of a hard stop. Disabled otherwise so the wallet, deposit,
    // and per-method-row consumers of this hook don't fetch a list they never read.
    const { data: withdrawMethods } = useWithdrawalMethods({
        enabled: isNonWithdrawableCheckEnabled && !isTrustlyVerificationEnabled,
        select: selectAllWithdrawMethods,
    });
    const hasWithdrawableMethodOnFile = (withdrawMethods ?? []).some(
        method => !method.via_site_id || !method.closed_loop_completed
    );

    const TRUSTLY_VERIFIED = trustlyInfo?.TRUSTLY_VERIFIED;
    const HAS_RESTRICTED_BALANCE = trustlyInfo?.HAS_RESTRICTED_BALANCE;
    const IS_IDPV = trustlyInfo?.IS_IDPV;
    const lightSdd = trustlyInfo?.LIGHT_SDD;
    const needsVerificationByLightSdd =
        lightSdd === LightSdd.NO_REQUIRED ||
        (lightSdd === LightSdd.NO && !TRUSTLY_VERIFIED && !IS_IDPV && HAS_RESTRICTED_BALANCE);
    // Either verification outcome lifts the closed-loop restriction for a restricted balance.
    const isVerifiedWithRestrictedBalance = (TRUSTLY_VERIFIED || IS_IDPV) && HAS_RESTRICTED_BALANCE;

    const outcome = resolveWithdrawVerificationOutcome({
        masterEnabled: isNonWithdrawableCheckEnabled,
        trustlyPathEnabled: isTrustlyVerificationEnabled,
        idpvPathEnabled: isIdpvVerificationEnabled,
        lightSdd,
        trustlyVerified: !!TRUSTLY_VERIFIED,
        idpvCompleted: !!IS_IDPV,
        hasRestrictedBalance: !!HAS_RESTRICTED_BALANCE,
        hasWithdrawableMethodOnFile,
    });

    const isVerificationCheckLoading = isNonWithdrawableCheckEnabled && isTrustlyInfoLoading;

    const shouldPerformTrustlyVerificationCheck =
        outcome === WithdrawVerificationOutcome.TRUSTLY_ONLY || outcome === WithdrawVerificationOutcome.CHOICE;

    return {
        outcome,
        isVerificationCheckLoading,
        shouldPerformTrustlyVerificationCheck,
        isVerifiedWithRestrictedBalance,
        needsVerificationByLightSdd,
        isUserLightSdd: lightSdd === LightSdd.YES,
    };
};
